import React, { useEffect, useState } from 'react';
import { scheduleService } from '../../services/schedule.service';
import { courseService } from '../../services/course.service';
import { planService } from '../../services/plan.service';
import { transcriptService } from '../../services/transcript.service';
import { Schedule, Course, AcademicPlan, Transcript, ScheduleCourse } from '../../types';
import { Modal } from '../UI/Modal';
import { Toast } from '../UI/Toast';
import {
  PlusIcon,
  CloseIcon,
  TrashIcon,
  SearchIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  LayersIcon,
} from '../UI/Icons';

// Helper to determine order of seasons
const getSeasonValue = (term: string) => {
  const t = term.toLowerCase();
  if (t.includes('winter')) return 0;
  if (t.includes('spring')) return 1;
  if (t.includes('summer')) return 1;
  if (t.includes('fall')) return 2;
  return 3;
};

const TERM_NUMBER_OPTIONS = [
  '1A', '1B', '2A', '2B', '3A', '3B', '4A', '4B',
  'WT1', 'WT2', 'WT3', 'WT4', 'WT5', 'WT6',
];

// Helper: Parse "Fall 2024" or "2A Spring 2025"
const parseTermName = (name: string) => {
  const parts = name.split(' ');
  let year = parseInt(parts[parts.length - 1]);
  if (isNaN(year)) year = 0;

  let term = 'Unknown';
  for (const p of parts) {
    if (['Winter', 'Spring', 'Fall', 'Summer'].includes(p)) {
      term = p;
      break;
    }
  }
  return { term, year };
};

const ScheduleView: React.FC = () => {
  const [plans, setPlans] = useState<AcademicPlan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<AcademicPlan | null>(null);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  // UI State
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showAddTermModal, setShowAddTermModal] = useState(false);

  const [newPlanName, setNewPlanName] = useState('');
  const [newTermYear, setNewTermYear] = useState<number>(new Date().getFullYear());
  const [newTermSeason, setNewTermSeason] = useState('Fall');
  const [newTermNumber, setNewTermNumber] = useState<string>('');

  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [courseSearch, setCourseSearch] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansData, coursesData, transcriptsData, schedulesData] = await Promise.all([
        planService.getPlans(),
        courseService.getCourses(),
        transcriptService.getTranscripts(),
        scheduleService.getSchedules(),
      ]);
      setPlans(plansData);
      setCourses(coursesData);
      setTranscripts(transcriptsData);
      setSchedules(schedulesData);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await scheduleService.getSchedules();
      setSchedules(data);
    } catch (error) {
      console.error('Failed to load schedules:', error);
    }
  };

  const handleCreatePlanClick = () => {
    setNewPlanName('');
    setShowCreatePlanModal(true);
  };

  const handleConfirmCreatePlan = async () => {
    if (!newPlanName) {
      setToast({ message: 'Please enter a plan name', type: 'error' });
      return;
    }

    try {
      const newPlan = await planService.createPlan(newPlanName);
      setPlans([newPlan, ...plans]);
      setCurrentPlan(newPlan);
      setShowCreatePlanModal(false);
      setToast({ message: 'Plan created successfully', type: 'success' });
    } catch (error) {
      console.error('Failed to create plan', error);
      setToast({ message: 'Failed to create plan.', type: 'error' });
    }
  };

  const handleDeletePlan = async (plan: AcademicPlan, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete plan "${plan.name}"?`)) {
      try {
        await planService.deletePlan(plan.id);
        const remaining = plans.filter((p) => p.id !== plan.id);
        setPlans(remaining);
        if (currentPlan && currentPlan.id === plan.id) {
          setCurrentPlan(null);
        }
        setToast({ message: 'Plan deleted.', type: 'success' });
      } catch (e) {
        console.error(e);
        setToast({ message: 'Failed to delete plan', type: 'error' });
      }
    }
  };

  const handleAddTerm = async () => {
    if (!currentPlan) return;
    try {
      await scheduleService.createSchedule({
        term: newTermSeason,
        year: newTermYear,
        term_number: newTermNumber || null,
        is_planned: true,
        plan_id: currentPlan.id,
        course_ids: [],
      });
      setShowAddTermModal(false);
      loadSchedules();
      setToast({ message: 'Term added', type: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ message: 'Failed to add term', type: 'error' });
    }
  };

  const handleAddCourse = async (courseId: number) => {
    if (!selectedScheduleId) return;
    try {
      await scheduleService.addCourse(selectedScheduleId, courseId);
      setShowAddCourseModal(false);
      loadSchedules();
      setToast({ message: 'Course added', type: 'success' });
    } catch (error) {
      console.error('Failed to add course:', error);
      setToast({ message: 'Failed to add course.', type: 'error' });
    }
  };

  const handleLookupAndAddCourse = async () => {
    if (!selectedScheduleId || !courseSearch.trim()) return;
    setLookupLoading(true);
    try {
      const course = await courseService.lookupCourse(courseSearch);
      await scheduleService.addCourse(selectedScheduleId, course.id);
      if (!courses.some((c) => c.id === course.id)) {
        setCourses([...courses, course]);
      }
      setShowAddCourseModal(false);
      loadSchedules();
      setToast({ message: `Added ${course.code} to schedule`, type: 'success' });
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      setToast({
        message: typeof detail === 'string' ? detail : `Couldn't find that course`,
        type: 'error',
      });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleRemoveCourse = async (scheduleId: number, courseEntryId: number) => {
    try {
      await scheduleService.removeCourse(scheduleId, courseEntryId);
      loadSchedules();
    } catch (error) {
      console.error('Failed to remove course:', error);
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    if (window.confirm('Delete this term?')) {
      await scheduleService.deleteSchedule(id);
      loadSchedules();
    }
  };

  const openAddTermModal = () => {
    setNewTermNumber('');
    setShowAddTermModal(true);
  };

  const openAddCourseModal = (scheduleId: number) => {
    setSelectedScheduleId(scheduleId);
    setCourseSearch('');
    setShowAddCourseModal(true);
  };

  // --- DATA PREPARATION for TIMELINE ---

  const currentTranscript = transcripts.length > 0 ? transcripts[0] : null;

  const historyItems = (currentTranscript?.terms || []).map((term, idx) => {
    const { term: season, year } = parseTermName(term.name);
    return {
      id: -1 * (idx + 1),
      term: season,
      year: year,
      term_number: (term.level ?? null) as string | null,
      is_planned: false,
      is_history: true,
      plan_id: null,
      courses: term.courses.map(
        (tc, cIdx) =>
          ({
            id: -1 * (cIdx + 1),
            course_code: tc.course_code,
            course_name: tc.course_name || '',
          } as ScheduleCourse)
      ),
    };
  });

  const planItems = currentPlan ? schedules.filter((s) => s.plan_id === currentPlan.id) : [];

  const combinedTimeline = [...historyItems, ...planItems].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return getSeasonValue(a.term) - getSeasonValue(b.term);
  });

  const filteredCourses = courses.filter(
    (c) =>
      c.code.toLowerCase().includes(courseSearch.toLowerCase()) ||
      c.name.toLowerCase().includes(courseSearch.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 lg:p-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ---------- Modals ---------- */}
      <Modal
        isOpen={showCreatePlanModal}
        onClose={() => setShowCreatePlanModal(false)}
        title="Create new academic plan"
        footer={
          <>
            <button className="btn-primary" onClick={handleConfirmCreatePlan}>
              Create plan
            </button>
            <button className="btn-secondary" onClick={() => setShowCreatePlanModal(false)}>
              Cancel
            </button>
          </>
        }
      >
        <p className="text-sm text-canvas-600">
          Give your plan a name so you can tell it apart from alternatives — co-op vs. regular, or a
          minor option.
        </p>
        <input
          type="text"
          className="input-field mt-4"
          placeholder="e.g. Option B (Minor in CS)"
          value={newPlanName}
          onChange={(e) => setNewPlanName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirmCreatePlan();
          }}
          autoFocus
        />
      </Modal>

      <Modal
        isOpen={showAddTermModal}
        onClose={() => setShowAddTermModal(false)}
        title="Add a term"
        footer={
          <>
            <button className="btn-primary" onClick={handleAddTerm}>
              Add term
            </button>
            <button className="btn-secondary" onClick={() => setShowAddTermModal(false)}>
              Cancel
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-field">Year</label>
              <input
                type="number"
                value={newTermYear}
                onChange={(e) => setNewTermYear(parseInt(e.target.value))}
                className="input-field"
              />
            </div>
            <div>
              <label className="label-field">Term</label>
              <select
                value={newTermSeason}
                onChange={(e) => setNewTermSeason(e.target.value)}
                className="input-field"
              >
                <option value="Winter">Winter</option>
                <option value="Spring">Spring</option>
                <option value="Fall">Fall</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-field">
              Term number <span className="font-normal text-canvas-500">(optional)</span>
            </label>
            <select
              value={newTermNumber}
              onChange={(e) => setNewTermNumber(e.target.value)}
              className="input-field"
            >
              <option value="">None</option>
              <optgroup label="Study terms">
                {TERM_NUMBER_OPTIONS.filter((t) => !t.startsWith('WT')).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Work terms">
                {TERM_NUMBER_OPTIONS.filter((t) => t.startsWith('WT')).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="mt-1.5 text-xs text-canvas-500">
              Tag this term with your program sequence (e.g. 2B) or co-op work term. You can still
              add courses to a work term.
            </p>
          </div>
        </div>
      </Modal>

      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900 sm:text-3xl">
            Schedule Planner
          </h1>
          <p className="mt-1.5 text-sm text-canvas-600">
            {currentPlan ? `Viewing: ${currentPlan.name}` : 'Choose a plan to lay out your terms'}
          </p>
        </div>
        {currentPlan ? (
          <button onClick={() => setCurrentPlan(null)} className="btn-secondary">
            <ArrowLeftIcon width={16} height={16} />
            Back to plans
          </button>
        ) : (
          <button onClick={handleCreatePlanClick} className="btn-primary">
            <PlusIcon width={16} height={16} />
            New plan
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl bg-canvas-200/70" />
          ))}
        </div>
      ) : (
        <>
          {/* ---------- Plan selection ---------- */}
          {!currentPlan && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {plans.length === 0 && (
                <div className="card col-span-full flex flex-col items-center px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                    <CalendarIcon width={26} height={26} />
                  </span>
                  <p className="mt-4 font-display text-lg font-semibold text-canvas-900">
                    No plans yet
                  </p>
                  <p className="mt-1.5 max-w-sm text-sm text-canvas-600">
                    A plan holds the terms and courses you're mapping out — create one to start
                    building your schedule.
                  </p>
                  <button onClick={handleCreatePlanClick} className="btn-primary mt-5">
                    <PlusIcon width={16} height={16} />
                    Create your first plan
                  </button>
                </div>
              )}

              {plans.map((plan) => {
                const count = schedules.filter((s) => s.plan_id === plan.id).length;
                return (
                  <button
                    key={plan.id}
                    onClick={() => setCurrentPlan(plan)}
                    className="card group relative p-6 text-left transition-all hover:-translate-y-1 hover:border-primary-200 hover:shadow-card-hover"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 transition-colors group-hover:bg-ink group-hover:text-white">
                      <LayersIcon width={20} height={20} />
                    </span>
                    <h3 className="mt-4 font-display text-lg font-semibold text-canvas-900">
                      {plan.name}
                    </h3>
                    <p className="mt-1.5 min-h-[2.5rem] text-sm text-canvas-600">
                      {plan.description || 'No description provided.'}
                    </p>
                    <div className="mt-4 flex items-center justify-between border-t border-canvas-200 pt-3.5 text-sm">
                      <span className="text-canvas-500">
                        {count === 1 ? '1 term' : `${count} terms`}
                      </span>
                      <span className="inline-flex items-center gap-1 font-medium text-primary-700">
                        View plan
                        <ArrowRightIcon width={15} height={15} />
                      </span>
                    </div>

                    <span
                      onClick={(e) => handleDeletePlan(plan, e)}
                      className="absolute right-4 top-4 rounded-lg p-1.5 text-canvas-400 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100"
                      title="Delete plan"
                    >
                      <TrashIcon width={17} height={17} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ---------- Plan detail (timeline) ---------- */}
          {currentPlan && (
            <div className="custom-scrollbar mt-6 flex-1 overflow-x-auto pb-4">
              <div className="flex min-w-max gap-4">
                {combinedTimeline.length === 0 && (
                  <div className="card flex flex-col items-center px-24 py-16 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-canvas-100 text-canvas-500">
                      <CalendarIcon width={26} height={26} />
                    </span>
                    <p className="mt-4 text-sm text-canvas-600">
                      No terms in this plan yet — add one to get started.
                    </p>
                  </div>
                )}

                {combinedTimeline.map((schedule) => {
                  // @ts-ignore — history items carry a custom flag
                  const isHistory = schedule.is_history === true;

                  return (
                    <div
                      key={`sch-${schedule.id}`}
                      className={`flex w-64 shrink-0 flex-col overflow-hidden rounded-2xl border shadow-card ${
                        isHistory ? 'border-canvas-200 bg-canvas-50' : 'border-canvas-200 bg-white'
                      }`}
                    >
                      {/* Column header */}
                      <div
                        className={`flex items-center justify-between border-b px-4 py-3.5 ${
                          isHistory
                            ? 'border-canvas-200 bg-canvas-100'
                            : 'border-primary-100 bg-primary-50'
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-1.5">
                            {schedule.term_number && (
                              <span
                                className={`chip !px-1.5 !py-0.5 text-[11px] font-bold text-white ${
                                  schedule.term_number.startsWith('WT') ? 'bg-amber-500' : 'bg-ink'
                                }`}
                              >
                                {schedule.term_number}
                              </span>
                            )}
                            <span
                              className={`font-display text-sm font-semibold ${
                                isHistory ? 'text-canvas-700' : 'text-primary-800'
                              }`}
                            >
                              {schedule.term}
                            </span>
                          </div>
                          <div className="mt-0.5 text-xs text-canvas-500">{schedule.year}</div>
                        </div>

                        {isHistory ? (
                          <span className="chip bg-canvas-200 text-canvas-600">History</span>
                        ) : (
                          <button
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="rounded-lg p-1.5 text-canvas-400 transition-colors hover:bg-white hover:text-rose-500"
                            title="Delete term"
                          >
                            <CloseIcon width={15} height={15} />
                          </button>
                        )}
                      </div>

                      {/* Courses */}
                      <div className="flex min-h-[280px] flex-1 flex-col gap-2 p-3">
                        {schedule.courses.length === 0 && (
                          <p className="py-6 text-center text-xs text-canvas-400">No courses yet</p>
                        )}

                        {schedule.courses.map((course) => (
                          <div
                            key={`crs-${course.id}`}
                            className="group relative rounded-xl border border-canvas-200 bg-white p-3 shadow-card transition-all hover:border-primary-200 hover:shadow-card-hover"
                          >
                            <div className="text-sm font-semibold text-canvas-900">
                              {course.course_code}
                            </div>
                            <div className="truncate text-xs text-canvas-500">
                              {course.course_name}
                            </div>

                            {!isHistory && (
                              <button
                                onClick={() => handleRemoveCourse(schedule.id, course.id)}
                                className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-canvas-300 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100"
                                aria-label="Remove course"
                              >
                                <CloseIcon width={12} height={12} />
                              </button>
                            )}
                          </div>
                        ))}

                        {!isHistory && (
                          <button
                            onClick={() => openAddCourseModal(schedule.id)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-canvas-200 py-2.5 text-sm font-medium text-canvas-500 transition-all hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                          >
                            <PlusIcon width={15} height={15} />
                            Add course
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add term column */}
                <button
                  onClick={openAddTermModal}
                  className="flex min-h-[380px] w-40 shrink-0 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-canvas-200 bg-white/50 text-canvas-400 transition-all hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-current">
                    <PlusIcon width={22} height={22} />
                  </span>
                  <span className="mt-2.5 text-sm font-medium">Add term</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---------- Add course modal ---------- */}
      {showAddCourseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-canvas-900/40 backdrop-blur-sm"
            onClick={() => setShowAddCourseModal(false)}
          />
          <div className="relative flex max-h-[80vh] w-full max-w-lg animate-fade-up flex-col overflow-hidden rounded-3xl bg-white shadow-shell">
            <div className="flex items-center justify-between px-6 pt-6">
              <h2 className="font-display text-lg font-semibold text-canvas-900">Add a course</h2>
              <button
                onClick={() => setShowAddCourseModal(false)}
                className="rounded-xl p-1.5 text-canvas-500 transition-colors hover:bg-canvas-100 hover:text-canvas-900"
                aria-label="Close"
              >
                <CloseIcon width={18} height={18} />
              </button>
            </div>

            <div className="px-6 pt-5">
              <div className="relative">
                <SearchIcon
                  width={17}
                  height={17}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-canvas-500"
                />
                <input
                  type="text"
                  placeholder="Search or type a course code (e.g. CS 246)"
                  className="input-field pl-11"
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLookupAndAddCourse();
                  }}
                  autoFocus
                />
              </div>

              {courseSearch.trim() && (
                <button
                  onClick={handleLookupAndAddCourse}
                  disabled={lookupLoading}
                  className="mt-2.5 flex w-full items-center justify-between rounded-xl bg-primary-50 px-4 py-2.5 text-sm text-primary-800 transition-colors hover:bg-primary-100 disabled:opacity-50"
                >
                  <span>
                    {lookupLoading ? (
                      'Looking up…'
                    ) : (
                      <>
                        Look up <span className="font-semibold">“{courseSearch.trim()}”</span> in the
                        UW catalog
                      </>
                    )}
                  </span>
                  <ArrowRightIcon width={15} height={15} />
                </button>
              )}
            </div>

            <div className="custom-scrollbar mt-4 min-h-0 flex-1 overflow-y-auto border-t border-canvas-200 px-6 py-4">
              {filteredCourses.length === 0 ? (
                <p className="py-8 text-center text-sm text-canvas-500">
                  No cached courses match — use the lookup above to fetch any course.
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredCourses.map((course) => (
                    <div
                      key={course.id}
                      className="group flex items-center justify-between gap-3 rounded-xl bg-canvas-50 p-3 transition-colors hover:bg-canvas-100"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-canvas-900">
                            {course.code}
                          </span>
                          <span className="chip bg-white text-canvas-600">{course.credits} cr</span>
                        </div>
                        <p className="truncate text-sm text-canvas-600">{course.name}</p>
                      </div>
                      <button
                        onClick={() => handleAddCourse(course.id)}
                        className="btn-primary shrink-0 !px-4 !py-2 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleView;
